/**
 * After a vehicle pickup, tell the mounted operational surfaces — on this
 * device and the shop's other devices — to re-read ticket truth.
 *
 * A pickup writes the TICKET (picked_up_at, status), never the custody row,
 * so custody realtime does not fire for it. Without this an FOH queue on
 * another device kept showing a car that had left. This is the existing
 * shared signal (`notifyDayboardInvalidated`: local `ticket-updated` + the
 * shop's dayboard broadcast) — no pickup-specific event, no new channel.
 *
 * The signal is a "re-read" hint, never authority: receivers refetch and the
 * database pickup result decides what they show.
 *
 * Emitted ONLY for a NEW pickup the server confirmed in response to this
 * click (`didPickUpNow` / `didPickUp`), with the server's canonical shop.
 *   - ALREADY_PICKED_UP → no (it already left; the caller still refreshes
 *     its own board).
 *   - refused           → no (nothing changed).
 *   - unknown / failed  → no (the caller keeps its reload-not-replay path).
 */
import { notifyDayboardInvalidated } from '@/utils/dayboardInvalidation';

export type PickupAct = 'completed' | 'incomplete';

export function notifyPickupChanged(p: {
  /** The caller's `didPickUpNow(outcome)` / `didPickUp(outcome)`. */
  isNewPickup: boolean;
  ticketId: string;
  /** Canonical shop from the pickup authority (never the React context). */
  shopId: string | null;
  pickedUpAt: string | null;
  act: PickupAct;
}): boolean {
  if (!p.isNewPickup || !p.shopId) return false;
  void notifyDayboardInvalidated({
    reason: p.act === 'completed' ? 'vehicle-picked-up' : 'vehicle-picked-up-incomplete',
    shopId: p.shopId,
    ticketId: p.ticketId,
    metadata: { pickedUpAt: p.pickedUpAt },
  });
  return true;
}
